import React, { Component } from 'react';
import { Helmet } from 'react-helmet';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { parse } from 'qs';
import { Button } from 'antd';
import {
  ArrowLeftOutlined,
  EditOutlined,
  SelectOutlined,
  FolderOpenOutlined,
  BellOutlined,
  TagOutlined,
} from '@ant-design/icons';
import { openModal } from '../actions/modal';
import { captureAnalytics } from '../actions/preload';
import Header from '../components/header';
import Viewer from '../components/viewer/ViewerContainer';

import {
  loadChromeViewerRecord,
  loadChromeViewerSelection,
  saveChromeViewerAnnotations,
} from '../actions/chromeViewerRecords';
import ChromeViewerRequestLifecycle, {
  isUsableChromeViewerSignedUrl,
} from '../utils/chromeViewerRequestLifecycle';
import { ownershipTupleMatches } from '../utils/chromeViewerRequestOwnership';
import '../components/main.scss';
import '../components/viewer/viewer.scss';

const ChromeViewerRecovery = ({
  actions,
  heading,
  headingRef,
  message,
}) => (
  <div className="error" role="alert">
    <h2 ref={headingRef} className="error-message" tabIndex="-1">
      {heading}
    </h2>
    <div className="error-message">
      {message}
    </div>
    {actions}
  </div>
);

ChromeViewerRecovery.propTypes = {
  actions: PropTypes.node.isRequired,
  heading: PropTypes.string.isRequired,
  headingRef: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.shape({ current: PropTypes.any }),
  ]).isRequired,
  message: PropTypes.string.isRequired,
};

class RecordViewer extends Component {
  constructor(props) {
    super(props);
    this.state = {
      loading: false,
      recordBlobObject: null,
    };
    this.requestLifecycle = new ChromeViewerRequestLifecycle(
      (...args) => this.props.loadChromeViewerRecord(...args),
    );
    this.lastFocusKey = null;
    this.loadingRef = React.createRef();
    this.recoveryHeadingRef = React.createRef();
    this.rootRef = React.createRef();
  }

  componentDidMount() {
    this.requestLifecycle.mount();
    this.focusPresentation();
    this.loadCurrentRoute();
  }

  componentDidUpdate(prevProps) {
    const previousRouteId = prevProps.match.params.id;
    const routeId = this.props.match.params.id;

    if (previousRouteId !== routeId) {
      this.requestLifecycle.resetRoute();
      this.setState({
        loading: false,
        recordBlobObject: null,
      }, this.loadCurrentRoute);
      return;
    }

    // Drop the previously-captured PDF blob when the record identity changes
    // (navigation, or fileToken rotation after a save persists the edits) so
    // no stale blob from the previous document can reach a save flow
    // (R25 / AE7). This surface keeps no unsaved-signature flag.
    if (prevProps.fileToken !== this.props.fileToken && this.state.recordBlobObject) {
      this.setState({ recordBlobObject: null });
    }

    this.focusPresentation();
  }

  componentWillUnmount() {
    this.requestLifecycle.unmount();
  }

  loadCurrentRoute = () => this.requestLifecycle.load(this.props.match.params.id);

  ownsCurrentRequest = (ownership = this.props.currentRecordRequest) => {
    return this.requestLifecycle.owns(ownership, this.props.match.params.id);
  };

  presentation = () => this.requestLifecycle.presentation(
    this.props.currentRecordRequest,
    this.props.match.params.id,
    this.props.signedUrl,
  );

  focusPresentation = () => {
    const presentation = this.presentation();
    const request = this.props.currentRecordRequest || {};
    const focusKeyParts = [
      this.props.match.params.id,
      this.requestLifecycle.routeGeneration,
      presentation,
    ];
    if (presentation !== 'loading') {
      focusKeyParts.push(request.attemptToken || 'idle');
    }
    const focusKey = focusKeyParts.join(':');
    if (focusKey === this.lastFocusKey) return;

    let target = null;
    if (presentation === 'loading') target = this.loadingRef.current;
    if (presentation === 'failure' || presentation === 'missing') {
      target = this.recoveryHeadingRef.current;
    }
    if (presentation === 'ready') {
      target = this.rootRef.current?.querySelector('.main-header-text');
      target?.setAttribute('tabindex', '-1');
    }

    if (target) {
      target.focus();
      this.lastFocusKey = focusKey;
    }
  };

  retry = () => {
    this.lastFocusKey = null;
    this.requestLifecycle.retry(this.props.match.params.id, this.presentation());
  };

  selectorType = () => {
    const queryParams = parse(this.props.location.search, { ignoreQueryPrefix: true });
    return queryParams.from?.toLowerCase() === 'uploaded' ? 'uploaded' : 'received';
  };

  selectorPath = () => (
    this.selectorType() === 'uploaded'
      ? '/app/chrome/uploaded_selector'
      : '/app/chrome/received_selector'
  );

  backToSelector = () => {
    captureAnalytics('chrome_viewer_back_to_selector_clicked', {
      selector: this.selectorType(),
    });
    this.props.history.replace({
      pathname: this.selectorPath(),
      state: { focusChromeSelectorHeading: true },
    });
  };

  editPatientOpen = () => {
    const patient = {
      file_token: this.props.fileToken,
      patient_name: this.props.patientName,
      patient_first_name: this.props.patientFirstName,
      patient_middle_name: this.props.patientMiddleName,
      patient_last_name: this.props.patientLastName,
      patient_dob: this.props.patientDOB,
      patient_phone_number: this.props.patientPhoneNumber,
      privacy_level: this.props.privacyLevel,
      can_edit_privacy_level: this.props.canEditPrivacyLevel,
      can_edit_patient_info: this.props.canEditPatientInfo,
      document_title: this.props.documentTitle,
      document_category: this.props.documentCategory,
      referral_note: this.props.referralNote,
      referring_provider: this.props.referringProvider,
      provider_office: this.props.referringProviderOffice,
      labels: this.props.selectedLabels,
    };

    const queryParams = parse(this.props.location.search, { ignoreQueryPrefix: true });
    const fromPage = !queryParams.from ? queryParams.from : queryParams.from.toLowerCase();

    const modalType = {
      type: 'EDIT_PATIENT',
      data: {
        patient,
        fromPage,
        selectedLabelsRecordViewer: this.props.selectedLabels,
        receiverType: this.props.receiverType,
        record: this.props.sendToken,
      },
    };
    this.props.openModal(modalType);
  };

  openNotifyModal = () => {
    const { sendToken, openModal: handleOpenModal } = this.props;
    const modalType = {
      type: 'NOTIFY',
      data: {
        record: sendToken,
      },
    };
    return handleOpenModal(modalType);
  };

  editLabel = () => {
    const {
      sendToken,
      openModal: handleOpenModal,
      selectedLabels,
      receiverType,
      hasWrongLabelingBeenReported,
      aiStatus,
      isLabelEdited,
    } = this.props;
    const modalType = {
      type: 'ADD_LABEL_TO_RECORD',
      data: {
        record: sendToken,
        selectedLabelsRecordViewer: selectedLabels,
        receiverType,
        reportAiDetails: {
          hasWrongLabelingBeenReported,
          aiStatus,
          isLabelEdited,
        },
      },
    };
    return handleOpenModal(modalType);
  };

  selectionOwnership = () => {
    const request = this.props.currentRecordRequest || {};
    if (this.presentation() !== 'ready' || !this.ownsCurrentRequest(request)) return null;

    return {
      attemptToken: request.attemptToken,
      routeOwnerId: this.props.match.params.id,
      routeGeneration: this.requestLifecycle.routeGeneration,
    };
  };

  selectionOwnershipIsCurrent = (ownership) => (
    ownership
    && ownershipTupleMatches(this.props.currentRecordRequest, ownership)
    && ownership.routeOwnerId === this.props.match.params.id
    && ownership.routeGeneration === this.requestLifecycle.routeGeneration
    && this.presentation() === 'ready'
  );

  annotationSaveCanDispatch = (operation, ownership) => (
    this.requestLifecycle.annotationSaveCanDispatch(
      operation,
      this.selectionOwnershipIsCurrent(ownership),
    )
  );

  selectRecord = async (archive) => {
    if (this.requestLifecycle.selectionIsBusy()) return;

    const ownership = this.selectionOwnership();
    if (!ownership) return;

    const { recordBlobObject } = this.state;
    if (recordBlobObject) {
      const saveResult = await this.beginSaveAnnotationsOnRecord(
        recordBlobObject,
        ownership.routeOwnerId,
        ownership,
      );
      if (!saveResult?.success || !this.selectionOwnershipIsCurrent(ownership)) return;
    }

    if (!this.selectionOwnershipIsCurrent(ownership)) return;

    const signal = this.requestLifecycle.startSelectionFetch(ownership);
    if (!signal) return;
    try {
      const result = await this.props.loadChromeViewerSelection(
        ownership.routeOwnerId,
        {
          archive,
          signal,
        },
      );
      if (!result?.success || !this.selectionOwnershipIsCurrent(ownership)) return;

      const responseRecord = result.data || {};
      const signedUrl = responseRecord.signed_url;
      if (!isUsableChromeViewerSignedUrl(signedUrl)) return;

      const name = responseRecord.patient_name || '';
      const dob = responseRecord.patient_dob || '';
      let fileName = name ? name.replace(/[^a-zA-Z0-9]+/g, '_') : 'record';
      fileName += dob ? `_${dob.replace(/[^a-zA-Z0-9]+/g, '_')}.pdf` : '.pdf';

      if (!this.selectionOwnershipIsCurrent(ownership)) return;
      document.dispatchEvent(new CustomEvent('chromeExtFileSelected', {
        detail: {
          url: signedUrl,
          fileName,
          documentTitle: responseRecord.document_title,
        },
      }));
    } finally {
      this.requestLifecycle.finishSelectionFetch(ownership);
    }
  };

  beginSaveAnnotationsOnRecord = async (file, recordId, selectionOwnership = null) => {
    // Save edits to the record, sets the state on recordHasunsavedEdits to false to allow
    // the record to be sent. We don't allow users to send files that have unsaved edits.
    const saveOwnership = selectionOwnership || this.selectionOwnership();
    if (!saveOwnership
      || !this.selectionOwnershipIsCurrent(saveOwnership)
      || saveOwnership.routeOwnerId !== recordId) {
      return { success: false, cancelled: true };
    }

    const saveOperation = this.requestLifecycle.startAnnotationSave(saveOwnership);
    if (!saveOperation) {
      return {
        success: false,
        cancelled: true,
        busy: true,
      };
    }

    this.setState({ loading: true });
    const result = await this.props.saveChromeViewerAnnotations(
      file,
      recordId,
      {
        ownership: saveOwnership,
        shouldDispatchTerminal: () => (
          this.annotationSaveCanDispatch(saveOperation, saveOwnership)
        ),
        onTerminalDispatched: () => (
          this.requestLifecycle.markAnnotationSaveTerminalDispatched(saveOperation)
        ),
      },
    );
    const saveIsCurrent = (
      this.requestLifecycle.mounted
      && this.requestLifecycle.annotationSaveIsCurrent(saveOperation)
      && this.selectionOwnershipIsCurrent(saveOwnership)
    );
    if (this.requestLifecycle.mounted && saveIsCurrent) {
      this.setState({ loading: false });
    }
    this.requestLifecycle.finishAnnotationSave(saveOperation);
    if (!saveIsCurrent) {
      return {
        ...result,
        success: false,
        cancelled: true,
      };
    }
    /* global toastr */
    if (result?.success) {
      toastr.success('Changes saved');
      return { ...result, success: true };
    }
    if (!result?.canceled && !result?.cancelled) {
      toastr.error('Unable to save changes. Please try again');
    }
    return {
      ...result,
      success: false,
      cancelled: Boolean(result?.canceled || result?.cancelled),
    };
  };

  updateRecordBlob = (file) => {
    // Updates the file blob saved in state, run on initial load and any when changes are made
    // after that. Passed into beginSaveAnnotationsOnRecord() when document is autosaved when
    // user tries to save.
    this.setState({
      recordBlobObject: file,
    });
  };

  recoveryCopy = () => {
    if (this.presentation() === 'missing') {
      return {
        heading: 'Document unavailable',
        message: 'This record does not have a document available to display. Please try again.',
      };
    }

    const status = this.props.currentRecordRequest?.errorCode;
    if (status === 403) {
      return {
        heading: 'Unable to load document',
        message: 'You don\'t have permission to view this document.',
      };
    }
    if (status === 404) {
      return {
        heading: 'Unable to load document',
        message: 'This document is no longer available.',
      };
    }
    return {
      heading: 'Unable to load document',
      message: 'We couldn\'t load this document. Please try again.',
    };
  };

  loadingOrRecoveryHeader = () => (
    <Header
      main="Document Viewer"
      subtext=""
      locationPath={[]}
      buttons={[{
        text: 'Back to selector',
        icon: <ArrowLeftOutlined />,
        disabled: false,
        func: this.backToSelector,
      }]}
    />
  );

  readyHeader = ({
    canEditPatientInfo,
    canEditPrivacyLevel,
    record,
    sendToken,
  }) => (
    <Header
      main="Document Viewer"
      subtext=""
      locationPath={[]}
      buttons={[
        {
          text: 'Back',
          icon: <ArrowLeftOutlined />,
          disabled: false,
          func: () => this.props.history.goBack(),
        },
        {
          text: 'Edit Record Info',
          icon: <EditOutlined />,
          disabled: (!canEditPrivacyLevel && !canEditPatientInfo) || !record,
          func: this.editPatientOpen,
        },
        {
          text: 'Notify',
          icon: <BellOutlined />,
          hide: !sendToken || !record,
          disabled: false,
          func: this.openNotifyModal,
        },
        {
          text: 'Add/Edit Label',
          icon: <TagOutlined />,
          disabled: false,
          func: this.editLabel,
        },
        {
          text: 'Choose Record',
          icon: <SelectOutlined />,
          disabled: false,
          func: () => this.selectRecord(false),
          secondary: true,
        },
        {
          text: 'Choose & Archive',
          icon: <FolderOpenOutlined />,
          disabled: false,
          func: () => this.selectRecord(true),
          secondary: true,
        },
      ]}
    />
  );

  render() {
    const {
      record,
      canEditPrivacyLevel,
      canEditPatientInfo,

      recordHasUnsavedEdits,
      signedUrl,
      signatureData,
      sendToken,
      nutrientViewer,
    } = this.props;

    const { loading } = this.state;
    const presentation = this.presentation();
    const viewerSaveOwnership = presentation === 'ready' ? this.selectionOwnership() : null;
    const recoveryCopy = presentation === 'failure' || presentation === 'missing'
      ? this.recoveryCopy()
      : null;

    return (
      <div ref={this.rootRef}>
        <Helmet>
          <title>Record Viewer - Medsender</title>
        </Helmet>
        <div
          style={{ marginLeft: '0' }}
          className="record-viewer"
        >
          {presentation === 'ready'
            ? this.readyHeader({
              canEditPatientInfo,
              canEditPrivacyLevel,
              record,
              sendToken,
            })
            : this.loadingOrRecoveryHeader()}
          {presentation === 'loading' && (
            <div
              ref={this.loadingRef}
              role="status"
              aria-live="polite"
              tabIndex="-1"
            >
              Loading document…
            </div>
          )}
          {recoveryCopy && (
            <ChromeViewerRecovery
              heading={recoveryCopy.heading}
              headingRef={this.recoveryHeadingRef}
              message={recoveryCopy.message}
              actions={(
                <div>
                  <Button type="primary" onClick={this.retry}>
                    Retry
                  </Button>
                  <Button onClick={this.backToSelector}>
                    Back to selector
                  </Button>
                </div>
              )}
            />
          )}
          {/* Mount the viewer only once the record has loaded (signedUrl set).
              ViewerContainer pins its engine on first render for this recordId,
              and nutrient_viewer arrives with the record fetch — mounting earlier
              would pin the old engine before the flag is known and never re-pin
              (recordId is stable). nutrientViewerFlag is delivered as an
              authoritative prop (R24) because this chrome-extension iframe's
              third-party-partitioned localStorage can't see the login-time flags
              cache — the normal delivery path for authenticated surfaces. */}
          {presentation === 'ready' && (
            <Viewer
              viewerSurface="iframe"
              documentUrl={signedUrl}
              recordId={this.props.match.params.id}
              nutrientViewerFlag={nutrientViewer}
              // Saves rotate the record's file token in place
              // (FINISHED_SAVE_ANNOTATIONS_ON_RECORD); threading it keeps the
              // viewer's document identity (recordId + fileToken) precise on
              // this editing surface instead of recordId-only.
              fileToken={this.props.fileToken}
              canEditRecord={true}
              updateRecordBlob={this.updateRecordBlob}
              recordNeedsToBeSaved={recordHasUnsavedEdits}
              saveAnnotationsOnRecord={(file, recordId) => (
                this.beginSaveAnnotationsOnRecord(file, recordId, viewerSaveOwnership)
              )}
              saveLoading={loading}
              setClick={() => {}}
              signatureData={signatureData}
              attachmentsData={this.props.attachmentsData}
              viewer="User"
              user={this.props.name}
              canGetandUpdateSignatures={true}
            />
          )}
        </div>
      </div>
    );
  }
}

RecordViewer.defaultProps = {
  signedUrl: '',
  fileToken: '',
  record: '',
  patientName: '',
  patientFirstName: '',
  patientMiddleName: '',
  patientLastName: '',
  patientDOB: '',
  patientPhoneNumber: '',
  documentTitle: '',
  documentCategory: '',
  referralNote: '',
  referringProvider: '',
  referringProviderOffice: '',
  selectedLabels: [],
  privacyLevel: '',
  sendToken: null,
  receiverType: null,
  nutrientViewer: false,
  currentRecordRequest: {},
};

RecordViewer.propTypes = {
  loadChromeViewerRecord: PropTypes.func.isRequired,
  loadChromeViewerSelection: PropTypes.func.isRequired,
  match: PropTypes.object.isRequired,
  history: PropTypes.shape({
    goBack: PropTypes.func.isRequired,
    replace: PropTypes.func.isRequired,
  }).isRequired,
  location: PropTypes.shape({
    search: PropTypes.string,
  }).isRequired,
  signedUrl: PropTypes.string,
  fileToken: PropTypes.string,
  record: PropTypes.string,
  nutrientViewer: PropTypes.bool,
  canEditPrivacyLevel: PropTypes.bool.isRequired,
  canEditPatientInfo: PropTypes.bool.isRequired,
  openModal: PropTypes.func.isRequired,
  privacyLevel: PropTypes.string,
  patientName: PropTypes.string,
  patientFirstName: PropTypes.string,
  patientMiddleName: PropTypes.string,
  patientLastName: PropTypes.string,
  patientDOB: PropTypes.string,
  selectedLabels: PropTypes.array,
  saveChromeViewerAnnotations: PropTypes.func.isRequired,
  patientPhoneNumber: PropTypes.string,
  documentTitle: PropTypes.string,
  documentCategory: PropTypes.string,
  referralNote: PropTypes.string,
  referringProvider: PropTypes.string,
  referringProviderOffice: PropTypes.string,
  recordHasUnsavedEdits: PropTypes.bool.isRequired,
  signatureData: PropTypes.array.isRequired,
  attachmentsData: PropTypes.array.isRequired,
  sendToken: PropTypes.string,
  receiverType: PropTypes.string,
  aiStatus: PropTypes.string.isRequired,
  hasWrongLabelingBeenReported: PropTypes.bool.isRequired,
  isLabelEdited: PropTypes.bool.isRequired,
  name: PropTypes.string.isRequired,
  currentRecordRequest: PropTypes.shape({
    attemptToken: PropTypes.string,
    completed: PropTypes.bool,
    error: PropTypes.bool,
    errorCode: PropTypes.number,
    loading: PropTypes.bool,
    mode: PropTypes.string,
    routeGeneration: PropTypes.string,
    routeOwnerId: PropTypes.string,
    settled: PropTypes.bool,
  }),
};

export default connect((state) => ({
  signedUrl: state.records.currentRecord,
  fileToken: state.records.fileToken,
  record: state.records.currentRecord,
  nutrientViewer: state.records.nutrientViewer,
  canEditPatientInfo: state.records.canEditPatientInfo,
  canEditPrivacyLevel: state.records.canEditPrivacyLevel,
  selectedLabels: state.records.selectedLabels,
  privacyLevel: state.records.privacyLevel,
  patientName: state.records.patientName,
  patientFirstName: state.records.patientFirstName,
  patientMiddleName: state.records.patientMiddleName,
  patientLastName: state.records.patientLastName,
  patientDOB: state.records.patientDOB,
  patientPhoneNumber: state.records.patientPhoneNumber,
  documentTitle: state.records.documentTitle,
  documentCategory: state.records.documentCategory,
  referralNote: state.records.referralNote,
  referringProvider: state.records.referringProvider,
  referringProviderOffice: state.records.referringProviderOffice,
  recordHasUnsavedEdits: state.records.recordHasUnsavedEdits,
  signatureData: state.records.signatureData,
  attachmentsData: state.records.attachmentsData,
  sendToken: state.records.sendToken,
  receiverType: state.records.receiverType,
  hasWrongLabelingBeenReported: state.records.hasWrongLabelingBeenReported,
  aiStatus: state.records.aiStatus,
  isLabelEdited: state.records.isLabelEdited,
  name: state.userProfile.name,
  currentRecordRequest: state.records.currentRecordRequest,
}), {
  loadChromeViewerRecord,
  loadChromeViewerSelection,
  openModal,
  saveChromeViewerAnnotations,
})(RecordViewer);
export { RecordViewer };
